import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { User } from '../types';
import { api, clearToken } from '../lib/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  login: (email: string, pass: string) => Promise<void>;
  register: (email: string, pass: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function useAuthProvider(): AuthContextType {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const u = await api.getMe();
      setUser(u);
      setError(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, pass: string) => {
    setError(null);
    try {
      const res = await api.login(email, pass);
      setUser(res.user);
    } catch (e: any) {
      setError(e.message || 'Login failed');
      throw e;
    }
  };

  const register = async (email: string, pass: string, name: string) => {
    setError(null);
    try {
      const res = await api.register(email, pass, name);
      setUser(res.user);
    } catch (e: any) {
      setError(e.message || 'Registration failed');
      throw e;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {}
    clearToken();
    setUser(null);
  };

  return {
    user,
    loading,
    error,
    setUser,
    login,
    register,
    logout,
    refreshUser,
  };
}
